// Hungarian postal-address parser for the "paste the full address" helper.
// Splits a single free-text line written in the canonical Hungarian order
//   irányítószám → település → közterület + házszám → ép. / lph. / em. / ajtó
// into the structured parts the unit/property forms store. Best-effort by
// design: whatever cannot be recognised stays in `street`, and the caller
// only *fills* form fields — the user can always correct the result.

const ROMAN = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };

/** "II" → "2"; returns the input unchanged when it isn't a roman numeral. */
function romanToArabic(s) {
  if (!/^[IVXLCDM]+$/i.test(s)) return s;
  let total = 0;
  let prev = 0;
  for (const ch of s.toUpperCase()) {
    const v = ROMAN[ch];
    total += v > prev ? v - 2 * prev : v;
    prev = v > prev ? 0 : v;
  }
  return String(total);
}

/**
 * Parse a pasted Hungarian address line.
 *
 * Handles e.g.
 *   "1061 Budapest, Andrássy út 12. B ép. 2. lph. 3. em. 5. ajtó"
 *   "1061 Budapest, Andrássy út 12. II/5."
 *   "Andrássy út 12. fszt. 2."
 *
 * @returns {{postal_code:string, city:string, street:string,
 *            house_number:string, building:string, staircase:string,
 *            floor:string, door:string}|null} null for blank input;
 *          unrecognised parts come back as ''.
 */
export function parseHungarianAddress(input) {
  let text = String(input || '').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  const out = {
    postal_code: '', city: '', street: '', house_number: '',
    building: '', staircase: '', floor: '', door: '',
  };

  // 1. Leading 4-digit postal code.
  const pm = text.match(/^(\d{4})[\s,.]+/);
  if (pm) {
    out.postal_code = pm[1];
    text = text.slice(pm[0].length);
  }

  // 2. City: everything up to the first comma. Without a comma we only
  //    dare to peel a city off when a postal code anchored the format —
  //    then the first capitalised word (plus an optional roman district,
  //    "Budapest VI.") is the settlement.
  const ci = text.indexOf(',');
  if (ci !== -1) {
    out.city = text.slice(0, ci).trim().replace(/\.$/, '');
    text = text.slice(ci + 1).trim();
  } else if (pm) {
    const cm = text.match(/^([A-ZÁÉÍÓÖŐÚÜŰ][\wáéíóöőúüű-]*(?: [IVXLCDM]+\.?)?)\s+(?=\S)/);
    if (cm) {
      out.city = cm[1].replace(/\.$/, '');
      text = text.slice(cm[0].length);
    }
  }

  // 3. Suffix parts, keyword-anchored ("B ép.", "2. lph.", "3. em.",
  //    "5. ajtó" — also "ajtó 5"). Each match is cut out of the text so
  //    step 4 only sees the street + house number.
  const take = (re) => {
    const m = text.match(re);
    if (!m) return '';
    text = (text.slice(0, m.index) + ' ' + text.slice(m.index + m[0].length))
      .replace(/\s+/g, ' ').trim();
    return (m[1] || '').replace(/[.,]+$/, '');
  };
  out.building = take(/(?:^|[\s,])([\wáéíóöőúüű\/-]+)\.?\s*(?:ép\.|ép(?=[\s,]|$)|épület)/i);
  out.staircase = take(/(?:^|[\s,])([\w\/-]+)\.?\s*(?:lph?\.|lph?(?=[\s,]|$)|lépcsőház)/i);
  out.door = take(/(?:^|[\s,])([\w\/-]+)\.?\s*(?:ajtó|ajto)/i)
    || take(/(?:ajtó|ajto)\s*:?\s*([\w\/-]+)/i);
  // Floor, with the common ". em. 12." shorthand where the trailing bare
  // number after the floor keyword is the door.
  const fm = text.match(/(?:^|[\s,])([\w]+)\.?\s*(?:em\.|em(?=[\s,]|$)|emelet)\.?(?:\s+(\d+[A-Za-z]?)\.?(?=[\s,]|$))?/i);
  if (fm) {
    out.floor = romanToArabic(fm[1].replace(/[.,]+$/, ''));
    if (out.door === '' && fm[2]) out.door = fm[2];
    text = (text.slice(0, fm.index) + ' ' + text.slice(fm.index + fm[0].length))
      .replace(/\s+/g, ' ').trim();
  } else if (take(/(?:^|[\s,])(m?f(?:öld)?szt?\.?|földszint)(?=[\s,]|$)/i) !== '') {
    out.floor = 'fszt.';
  }

  // 4. Street + house number, with the "II/5" floor/door shorthand that
  //    often trails the house number ("Fő utca 3. II/5.").
  text = text.replace(/^[\s,.]+|[\s,]+$/g, '');
  const sm = text.match(
    /^(.+?)[\s,]+(\d+(?:[\/-][\dA-Za-zÁ-űáéíóöőúüű]+)*)\.?(?:[\s,]+([IVXLCDM]+|\d+)\.?\s*\/\s*(\d+[A-Za-z]?)\.?)?$/,
  );
  if (sm) {
    out.street = sm[1].replace(/[\s,.]+$/, '');
    out.house_number = sm[2];
    if (sm[3] && sm[4]) {
      if (out.floor === '') out.floor = romanToArabic(sm[3]);
      if (out.door === '') out.door = sm[4];
    }
  } else {
    out.street = text.replace(/[\s,.]+$/, '');
  }

  return out;
}
